import React, { useState } from "react";
import { api, errMsg } from "../../lib/api";
import { Btn } from "../../components/UI";
import { toast } from "sonner";
import { X, Loader2, Mail, BellRing, Plus, Trash2 } from "lucide-react";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const DigestSettingsModal = ({ candidate, id, onClose, onSaved }) => {
  const [digestOn, setDigestOn] = useState(!!candidate.digest_enabled);
  const [alertsOn, setAlertsOn] = useState(!!candidate.alert_emails_enabled);
  const [recipients, setRecipients] = useState(candidate.digest_recipients || []);
  const [newEmail, setNewEmail] = useState("");
  const [saving, setSaving] = useState(false);

  const addEmail = () => {
    const e = newEmail.trim().toLowerCase();
    if (!EMAIL_RE.test(e)) { toast.error("Enter a valid email address"); return; }
    if (recipients.includes(e)) { toast.error("Already on the list"); return; }
    setRecipients([...recipients, e]);
    setNewEmail("");
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/political-intelligence/candidates/${id}`, {
        digest_enabled: digestOn, alert_emails_enabled: alertsOn, digest_recipients: recipients,
      });
      toast.success("Digest & alert settings saved");
      onSaved();
    } catch (e) { toast.error(errMsg(e, "Could not save settings")); }
    finally { setSaving(false); }
  };

  const Toggle = ({ on, set, testid }) => (
    <button onClick={() => set(!on)} data-testid={testid}
      className={`w-10 h-5.5 rounded-full relative transition-colors shrink-0 ${on ? "bg-emerald-400/80" : "bg-white/15"}`} style={{ height: 22 }}>
      <span className={`absolute top-0.5 w-4.5 h-4.5 rounded-full bg-white transition-all ${on ? "left-5" : "left-0.5"}`} style={{ width: 18, height: 18 }} />
    </button>
  );

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0a0e1a] border border-white/10 rounded-lg max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="digest-settings-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl font-bold">Digest & alerts</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-white" data-testid="digest-modal-close"><X className="w-4 h-4" /></button>
        </div>

        <div className="space-y-4">
          <div className="flex items-start justify-between gap-3 rounded-lg border border-white/10 p-3.5">
            <div>
              <div className="text-sm font-medium text-white inline-flex items-center gap-2"><Mail className="w-4 h-4 text-cyan-300" /> Weekly race digest</div>
              <p className="text-xs text-slate-500 mt-1">Every Monday 8 AM ET — polling movement, national environment, race news and campaign delivery.</p>
            </div>
            <Toggle on={digestOn} set={setDigestOn} testid="digest-enabled-toggle" />
          </div>

          <div className="flex items-start justify-between gap-3 rounded-lg border border-white/10 p-3.5">
            <div>
              <div className="text-sm font-medium text-white inline-flex items-center gap-2"><BellRing className="w-4 h-4 text-rose-300" /> Instant alert emails</div>
              <p className="text-xs text-slate-500 mt-1">Email the moment an intelligence alert fires — generic-ballot shifts, new poll coverage, news surges.</p>
            </div>
            <Toggle on={alertsOn} set={setAlertsOn} testid="alert-emails-toggle" />
          </div>

          <div className="rounded-lg border border-white/10 p-3.5">
            <div className="text-sm font-medium text-white">Recipients</div>
            <p className="text-xs text-slate-500 mt-1 mb-2.5">Digests and alerts go to everyone below. If the list is empty, they go to the race owner.</p>
            <div className="flex flex-wrap gap-1.5 mb-2.5">
              {recipients.length === 0 && <span className="text-xs text-slate-600">No team emails yet — owner will receive everything.</span>}
              {recipients.map(e => (
                <span key={e} className="text-xs px-2 py-1 rounded-full border border-cyan-400/30 bg-cyan-400/10 text-cyan-200 inline-flex items-center gap-1.5" data-testid={`recipient-chip-${e}`}>
                  {e}
                  <button onClick={() => setRecipients(recipients.filter(x => x !== e))} className="text-cyan-300/70 hover:text-rose-300" data-testid={`remove-recipient-${e}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addEmail()}
                placeholder="teammate@campaign.org" data-testid="recipient-email-input"
                className="flex-1 bg-slate-900/80 border border-white/15 rounded-md px-3 py-2 text-sm text-white focus:border-cyan-400/60 focus:outline-none placeholder-slate-500" />
              <Btn variant="secondary" onClick={addEmail} data-testid="add-recipient-btn"><Plus className="w-4 h-4" /> Add</Btn>
            </div>
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
          <Btn onClick={save} disabled={saving} data-testid="save-digest-settings-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Save settings
          </Btn>
        </div>
      </div>
    </div>
  );
};
